// Shared primitives for the loading skeletons. Keep the emitted HTML
// byte-aligned with the matching Phlex helpers in
// app/components/skeletons/helpers.rb so React can hydrate the
// server-rendered skeleton without restarting the shimmer animation.

import type { CSSProperties, ReactNode } from 'react';

export const SHIMMER = 'bg-dinnerplate-white-light animate-shimmer';
export const DEFAULT_LABEL = 'Loading…';

// Bypass cn()/tailwind-merge so the class string matches the Phlex literal
// interpolation byte-for-byte.
export function joinClasses(...parts: Array<string | undefined>) {
  return parts.filter(Boolean).join(' ');
}

export function Bar({ className, style }: { className?: string; style?: CSSProperties }) {
  return <div aria-hidden="true" style={style} className={joinClasses(SHIMMER, className)} />;
}

export function PageShell({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div role="status" aria-busy="true" aria-label={label}>
      <div className="flex flex-col h-screen overflow-hidden bg-off-white">{children}</div>
    </div>
  );
}

export function DetailHeader({ crumbs }: { crumbs: number }) {
  return (
    <header className="flex-shrink-0 h-23 border-b border-dinnerplate-white">
      <div className="flex h-full w-full items-center justify-between px-16">
        <div className="flex items-center gap-2">
          <Bar className="h-5 w-[62px]" />
          <Bar className="size-3" />
          <Bar className="h-5 w-[88px]" />
          {crumbs > 2 ? (
            <>
              <Bar className="size-3" />
              <Bar className="h-5 w-[25px]" />
            </>
          ) : null}
        </div>
        <Bar className="h-[45px] w-[194px] rounded-input" />
      </div>
    </header>
  );
}

export function DetailFooter({ buttonClass }: { buttonClass: string }) {
  return (
    <footer className="flex-shrink-0 h-23 border-t border-dinnerplate-white">
      <div className="flex h-full w-full items-center justify-end px-16">
        <Bar className={`${buttonClass} rounded-input`} />
      </div>
    </footer>
  );
}

export function ReadOnlyField() {
  return (
    <div>
      <Bar className="h-[17px] w-[72px]" />
      <div className="mt-2">
        <Bar className="h-5 w-[160px]" />
      </div>
    </div>
  );
}

export function TwoColumnReadOnly() {
  return (
    <div className="grid grid-cols-2 gap-x-12">
      <ReadOnlyField />
      <ReadOnlyField />
    </div>
  );
}

export function InputField() {
  return (
    <div>
      <Bar className="h-[17px] w-[72px] mb-2" />
      <Bar className="h-11 w-full rounded-input" />
    </div>
  );
}

export function TwoColumnInput() {
  return (
    <div className="grid grid-cols-2 gap-12">
      <InputField />
      <InputField />
    </div>
  );
}
